<template>
  <div class="aps aps-embedded">
    <section class="aps-chat-step">
      <div class="aps-chat-body">
        <div class="aps-chat-head">
          <div class="aps-head-left">
          <span class="aps-chat-brand">{{ brandLabel }} <span class="aps-rocket" :class="{ 'aps-rocket-launch': launching }">
            <span class="aps-rocket-glyph" @animationend="onRocketLaunchEnd">🚀</span>
            <span v-if="launching" class="aps-stars" aria-hidden="true">
              <span v-for="(s, i) in rocketStars" :key="i" class="aps-star"
                :style="{ left: s.x + 'px', top: s.y + 'px', fontSize: s.size, animationDelay: s.delay + 's' }">✦</span>
            </span></span></span>
          <span v-if="tool" class="aps-scopepill">{{ toolName }}</span>
          </div>
          <div class="aps-head-right">
            <div v-if="phase === 'chat'" class="aps-budget-meter" :title="tokensTodayLabel">
              <span class="aps-budget-meter-label">{{ tokenBudgetLabel }}</span>
              <span class="aps-budget-bar">
                <span class="aps-budget-bar-fill" :class="tokenBudgetClass" :style="{ width: tokenBudgetPct + '%' }"></span>
              </span>
            </div>
            <button v-if="phase === 'chat'" class="aps-chat-back" @click="resetConversation">Start over</button>
          </div>
        </div>

        <div class="aps-chat" ref="chatScroll">
          <div v-for="(m, i) in messages" :key="i" :class="'aps-msg aps-msg-' + m.role">
            <div v-if="m.role === 'assistant'" class="aps-bubble">
              <div v-if="m.lookedAt && m.lookedAt.length" class="aps-lookedat">Looked at: {{ m.lookedAt.join(', ') }}</div>
              <div v-html="renderMarkdown(m.content)"></div>
            </div>
            <div v-else class="aps-bubble">{{ m.content }}</div>
          </div>

          <!-- Conversational tool selection: 3 product buttons shown right after the welcome. -->
          <div v-if="phase === 'pickTool'" class="aps-toolpick">
            <button v-for="t in toolCards" :key="t.reference" type="button" class="aps-toolbtn"
              :style="{ '--tc': toolColor(t.reference) }" @click="establishTool(t)">{{ t.name }}</button>
          </div>

          <div v-if="thinking" class="aps-msg aps-msg-assistant">
            <div class="aps-bubble aps-thinking">
              <span class="aps-dots" aria-hidden="true"><span></span><span></span><span></span></span>
            </div>
          </div>
          <div v-if="chatError" class="aps-chat-error">{{ chatError }}</div>
        </div>

        <div class="aps-input-wrap">
          <textarea v-model="draft" class="aps-input" rows="1"
            :placeholder="phase === 'chat' ? 'Ask Peter…' : 'Choose a product above to start'"
            :disabled="isReplying || phase !== 'chat'" @keydown.enter.exact.prevent="send"></textarea>
          <button class="aps-send" :disabled="!draft.trim() || isReplying || phase !== 'chat'" @click="send">→</button>
        </div>
        <p class="aps-disclaimer">AI-generated from the product data, so it can be wrong. If in doubt, check the data tables.</p>
      </div>
    </section>
  </div>
</template>

<script>
import $ from 'jquery';
import {
  formatTokenCount,
  renderMarkdown as renderMd,
  splitForReveal,
} from './askPeterScope';

// Most recent chat messages resent as context each turn. Bounds prompt tokens on long
// conversations; the backend clamps to the same limit so a crafted payload can't exceed it.
var MAX_HISTORY_MESSAGES = 25;

export default {
  name: 'AskPeterScoped',
  props: ['userSelection', 'userDetails', 'brandName'],
  data: function () {
    return {
      phase: 'pickTool',       // 'pickTool' (welcome + 3 product buttons) | 'chat'
      tool: null,              // { toolReference, toolName } once the product is chosen by conversation
      tokensToday: null,
      dailyTokenLimit: 250000, // daily token budget for the depleting bar; the server sends the real value with /usage and enforces it
      messages: [],            // [{ role, content, lookedAt?, seed? }] — seed turns are canned, not sent as history
      draft: '',
      isReplying: false,
      thinking: false,         // network wait: shows the animated "thinking" dots
      revealTimer: null,       // setTimeout id for the typewriter reveal
      chatError: null,
      launching: false,        // rocket loop easter-egg (first visit per session)
      // Sparkles flung up the exhaust column as the rocket climbs: position (px) + stagger.
      rocketStars: [
        { x: 2, y: -4, size: '0.5em', delay: 0.5 },
        { x: -7, y: -16, size: '0.6em', delay: 0.58 },
        { x: 6, y: -30, size: '0.55em', delay: 0.66 },
        { x: -5, y: -44, size: '0.7em', delay: 0.74 },
        { x: 4, y: -60, size: '0.5em', delay: 0.82 },
        { x: -6, y: -78, size: '0.55em', delay: 0.9 },
        { x: 3, y: -98, size: '0.45em', delay: 0.98 },
        { x: -4, y: -120, size: '0.5em', delay: 1.06 },
      ],
    };
  },
  computed: {
    brandLabel: function () { return this.brandName || 'Peter'; },
    toolName: function () { return this.tool ? this.tool.toolName : ''; },
    toolCards: function () {
      // Same source DataTableToolSelection uses: non-pricing tools unless the firm has no pricing.
      var sel = this.userSelection || {};
      var list =
        sel.analysisType === 'DataTables' && this.userDetails && !this.userDetails.hasSetNoPricing
          ? (sel.nonPricingTools || [])
          : (sel.tools || []);
      // For now only Critical Illness, Life and Income Protection, in that order.
      var wanted = ['CIPlus', 'LifePro', 'IPPro'];
      return wanted
        .map(function (base) {
          var t = list.find(function (x) {
            return x.toolReference === base || x.toolReference === base + ' - Pricing';
          });
          return t ? { reference: t.toolReference, name: t.toolName } : null;
        })
        .filter(Boolean);
    },
    tokensTodayLabel: function () {
      if (this.tokensToday == null) return '—';
      return formatTokenCount(this.tokensToday) + ' tokens today';
    },
    // Daily-budget bar: how much is left, as a % (the bar shrinks as today's usage grows).
    tokensRemaining: function () {
      return Math.max(0, this.dailyTokenLimit - (this.tokensToday || 0));
    },
    tokenBudgetPct: function () {
      if (!this.dailyTokenLimit) return 100;
      var pct = 100 - ((this.tokensToday || 0) / this.dailyTokenLimit) * 100;
      return Math.max(0, Math.min(100, pct));
    },
    tokenBudgetLabel: function () {
      return formatTokenCount(this.tokensRemaining) + ' left today';
    },
    tokenBudgetClass: function () {
      var p = this.tokenBudgetPct;
      if (p <= 15) return 'aps-budget-bar-low';
      if (p <= 40) return 'aps-budget-bar-mid';
      return 'aps-budget-bar-ok';
    },
  },
  methods: {
    // Tool accent colours — same palette as the analysis cards.
    toolColor: function (reference) {
      if (reference.indexOf('CIPlus') === 0) return '#E54B6B';   // Critical Illness — red
      if (reference.indexOf('LifePro') === 0) return '#2EAA75';  // Life — green
      if (reference.indexOf('IPPro') === 0) return '#D4A015';    // Income Protection — gold
      return '#3B6FE3';
    },
    // The opening assistant message; the 3 product buttons render beneath it while picking.
    seedWelcome: function () {
      this.messages = [{
        role: 'assistant',
        seed: true,
        content: "Hi, I'm Peter, your data assistant. Which product would you like to explore?",
      }];
    },
    // Conversational tool selection: the user clicks one of the 3 product buttons. No network call —
    // the catalog/data are fetched server-side per question once we know the tool.
    // Rocket easter-egg: lift off the first time the panel is opened this browser session. Called by
    // the widget on open (the component itself stays mounted while minimised).
    launchRocketOnce: function () {
      try {
        if (!window.sessionStorage.getItem('aps-rocket-launched')) {
          window.sessionStorage.setItem('aps-rocket-launched', '1');
          var that = this;
          window.setTimeout(function () { that.launching = true; }, 400);
        }
      } catch (err) {
        // sessionStorage blocked (private mode): silently skip the effect.
      }
    },
    establishTool: function (card) {
      this.cancelReplyAnimations();
      this.tool = { toolReference: card.reference, toolName: card.name };
      this.phase = 'chat';
      this.chatError = null;
      // Warm the server-side catalogue for this product while the adviser types the first question
      // (a cold build fetches every data feature from APIv2 and takes a few seconds).
      $.ajax({ type: 'POST', url: 'api/ChatDataTable/warm/' + encodeURIComponent(card.reference) });
      this.messages.push({
        role: 'assistant',
        seed: true,
        content: 'Great — ' + card.name + '. Go ahead, ask me anything about the data.',
      });
      this.fetchTokensToday();
      this.scrollChatToBottom();
    },
    fetchTokensToday: function () {
      var that = this;
      $.ajax({
        type: 'GET',
        url: 'api/ChatDataTable/usage',
        success: function (response) {
          if (response && typeof response.tokensToday === 'number') that.tokensToday = response.tokensToday;
          if (response && typeof response.dailyTokenLimit === 'number' && response.dailyTokenLimit > 0) that.dailyTokenLimit = response.dailyTokenLimit;
        },
      });
    },
    renderMarkdown: function (text) {
      return renderMd(text);
    },
    // One call per turn: the server routes the question across the whole tool's catalog and answers.
    send: function () {
      var that = this;
      var question = (this.draft || '').trim();
      if (!question || this.isReplying || this.phase !== 'chat' || !this.tool) return;

      this.messages.push({ role: 'user', content: question });
      this.draft = '';
      this.chatError = null;
      this.isReplying = true;
      this.startThinking();
      this.scrollChatToBottom();

      // Resend only the real turns (skip the canned welcome/confirmation), most-recent-capped.
      var history = this.messages
        .filter(function (m) { return !m.seed; })
        .slice(0, -1)
        .slice(-MAX_HISTORY_MESSAGES)
        .map(function (m) { return { role: m.role, content: m.content }; });

      $.ajax({
        type: 'POST',
        url: 'api/ChatDataTable/converse',
        contentType: 'application/json',
        data: JSON.stringify({ toolReference: that.tool.toolReference, question: question, history: history }),
        success: function (resp) {
          that.stopThinking();
          if (resp && resp.error) { that.isReplying = false; that.chatError = resp.error; return; }
          if (resp && typeof resp.tokensToday === 'number') that.tokensToday = resp.tokensToday;
          var reply = (resp && resp.reply) || '';
          var lookedAt = (resp && resp.lookedAt) || [];
          if (!reply) { that.messages.push({ role: 'assistant', content: '(no reply)' }); that.isReplying = false; return; }
          // Small talk / no-match / "can't compare" all arrive as a plain reply — reveal them the same way.
          that.revealReply(reply, lookedAt);
        },
        error: function (xhr) {
          that.stopThinking();
          that.isReplying = false;
          if (xhr.status === 401) { window.location = './Api/signin'; return; }
          that.chatError = 'Could not reach Peter. Please try again.';
        },
      });
    },
    // --- Waiting + reveal UX (the reply arrives whole; we animate the wait and the reveal) ---
    startThinking: function () {
      this.thinking = true;
    },
    stopThinking: function () {
      this.thinking = false;
    },
    // Typewriter: append the reply piece by piece — words for prose, whole markdown tables at once.
    revealReply: function (fullText, lookedAt) {
      var that = this;
      var pieces = splitForReveal(fullText);
      this.messages.push({ role: 'assistant', content: '', lookedAt: lookedAt || [] });
      var idx = this.messages.length - 1;
      var k = 0;
      this.clearRevealTimer();
      var step = function () {
        if (k >= pieces.length) { that.isReplying = false; that.revealTimer = null; return; }
        that.messages[idx].content += pieces[k];
        // A table block is long → give it a slightly longer beat; single words type at a readable pace.
        var delay = pieces[k].length > 30 ? 220 : 45;
        k += 1;
        that.scrollChatToBottom();
        that.revealTimer = window.setTimeout(step, delay);
      };
      step();
    },
    clearRevealTimer: function () {
      if (this.revealTimer) { window.clearTimeout(this.revealTimer); this.revealTimer = null; }
    },
    // Stop both animations (on reset / navigate away).
    cancelReplyAnimations: function () {
      this.clearRevealTimer();
      this.thinking = false;
    },
    scrollChatToBottom: function () {
      var that = this;
      this.$nextTick(function () {
        var el = that.$refs.chatScroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    // "Start over": clear the conversation and go back to the product welcome.
    resetConversation: function () {
      this.cancelReplyAnimations();
      this.draft = '';
      this.chatError = null;
      this.isReplying = false;
      this.thinking = false;
      this.tool = null;
      this.phase = 'pickTool';
      this.seedWelcome();
    },
    // Once the rocket has blasted off the top, drop the class + sparkles so the glyph pops back.
    onRocketLaunchEnd: function (e) {
      if (e.animationName === 'aps-liftoff') this.launching = false;
    },
  },
  created: function () {
    this.seedWelcome();
  },
  beforeUnmount: function () {
    // Clear any running thinking/typewriter timers so they don't fire after the view is gone.
    this.cancelReplyAnimations();
  },
};
</script>

<style scoped>
.aps { max-width: 1180px; margin: 0 auto; padding: 16px 24px 80px; text-align: left;
  font-family: 'Inter', system-ui, sans-serif; color: #0d1117; }
.aps-rocket { font-size: 0.7em; position: relative; display: inline-block; }
.aps-rocket-glyph { display: inline-block; will-change: transform; }
/* First-visit easter-egg: the rocket shakes (countdown), then blasts straight up and off the
   top of the screen flinging a column of golden sparkles, then pops back into place. */
.aps-rocket-launch .aps-rocket-glyph {
  animation: aps-shake 0.5s ease-in-out,
             aps-liftoff 1.1s 0.5s cubic-bezier(0.45, 0, 0.85, 0.4) forwards;
}
@keyframes aps-shake {
  0%, 100% { transform: translate(0, 0) rotate(0); }
  20% { transform: translate(-1.5px, 1px) rotate(-8deg); }
  40% { transform: translate(1.5px, 0) rotate(8deg); }
  60% { transform: translate(-1.5px, 1px) rotate(-8deg); }
  80% { transform: translate(1.5px, 0) rotate(6deg); }
}
@keyframes aps-liftoff {
  0% { transform: translate(0, 4px) rotate(0) scale(1.05); opacity: 1; }
  18% { transform: translate(0, 0) rotate(0) scale(1); opacity: 1; }
  100% { transform: translate(8px, -150vh) rotate(8deg) scale(0.55); opacity: 0; }
}
.aps-stars { position: absolute; left: 50%; top: 50%; width: 0; height: 0; pointer-events: none; }
.aps-star {
  position: absolute;
  color: #ffd24a;
  line-height: 1;
  opacity: 0;
  transform: translate(-50%, -50%) scale(0);
  text-shadow: 0 0 6px rgba(255, 210, 74, 0.85);
  animation: aps-star-pop 0.9s ease-out forwards;
}
@keyframes aps-star-pop {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0) rotate(0deg); }
  35% { opacity: 1; transform: translate(-50%, -50%) scale(1.15) rotate(25deg); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(0.7) rotate(60deg); }
}
@media (prefers-reduced-motion: reduce) {
  .aps-rocket-launch .aps-rocket-glyph { animation: none; }
  .aps-star { animation: none; opacity: 0; }
}

/* Chat surface — dark "focus mode" card. */
.aps-chat-step { max-width: 860px; margin: 0 auto; background: #1c1c1e; border: 1px solid #2e2e34;
  border-radius: 18px; padding: 18px 20px 16px; color: #e7e7ea; }
.aps-chat-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
.aps-head-left { display: flex; align-items: center; gap: 8px; min-width: 0; flex-shrink: 1; }
.aps-head-left .aps-scopepill { overflow: hidden; text-overflow: ellipsis; }
.aps-head-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.aps-chat-back { background: transparent; border: none; color: #c9c9d0; cursor: pointer; white-space: nowrap;
  font-size: 13px; font-family: inherit; line-height: 1; padding: 4px 8px; border-radius: 6px; }
.aps-chat-back:hover { color: #fff; background: rgba(255, 255, 255, 0.06); }
.aps-chat-brand { display: inline-flex; align-items: center; gap: 4px; line-height: 1.2; white-space: nowrap; flex-shrink: 0; font-size: 14px; font-weight: 700; letter-spacing: -0.3px; color: #3B6FE3; margin: 0; }
.aps-scopepill { display: inline-block; font-size: 11px; line-height: 1.4; font-weight: 600; white-space: nowrap;
  color: #c9c9d0; background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px; padding: 2px 10px; }
/* Daily token budget bar — fill shows how much is LEFT and shrinks as usage grows. */
.aps-budget-meter { display: flex; align-items: center; gap: 8px; }
.aps-budget-meter-label { font-size: 11px; color: #d6d6dc; white-space: nowrap; }
.aps-budget-bar { width: 56px; height: 5px; border-radius: 999px; background: rgba(255, 255, 255, 0.12); overflow: hidden; }
.aps-budget-bar-fill { display: block; height: 100%; border-radius: 999px; transition: width .5s ease, background-color .5s ease; }
.aps-budget-bar-ok { background: #34d399; }
.aps-budget-bar-mid { background: #fbbf24; }
.aps-budget-bar-low { background: #f87171; }
.aps-chat { background: #18181a; border: 1px solid #2e2e34; border-radius: 16px; padding: 20px;
  min-height: 320px; max-height: 58vh; overflow-y: auto; }
.aps-msg { display: flex; margin-bottom: 10px; }
.aps-msg-user { justify-content: flex-end; }
.aps-bubble { max-width: 80%; padding: 9px 12px; border-radius: 14px; font-size: 13px; line-height: 1.45; }
.aps-msg-user .aps-bubble { background: #3b6fe3; color: #fff; }
.aps-msg-assistant .aps-bubble { background: #2a2a2e; border: 1px solid #34343a; color: #e7e7ea; }
/* Transparency line: which conditions the router looked at for this answer. */
.aps-lookedat { font-size: 11px; color: #8a8a92; font-style: italic; margin-bottom: 6px; }
/* Conversational tool selection buttons. */
.aps-toolpick { display: flex; flex-wrap: wrap; gap: 10px; margin: 4px 0 8px; }
.aps-toolbtn { appearance: none; -webkit-appearance: none; background: #262629; border: 1px solid #3a3a42;
  border-left: 3px solid var(--tc, #3B6FE3); border-radius: 10px; padding: 12px 18px; color: #f3f3f5;
  font-family: inherit; font-size: 14px; font-weight: 600; cursor: pointer;
  transition: background .15s, border-color .15s, transform .15s; }
.aps-toolbtn:hover { background: #303036; border-color: var(--tc, #6ea8fe); transform: translateY(-1px); }
/* Markdown tables in Peter's replies — real <table>, so the browser auto-aligns columns. */
.aps-msg-assistant .aps-bubble:has(.aps-md-table) { max-width: 100%; width: 100%; }
.aps-md-tablewrap { overflow-x: auto; margin: 8px 0; }
.aps-md-table { border-collapse: collapse; width: 100%; font-size: 12.5px; }
.aps-md-table th, .aps-md-table td { border: 1px solid #3a3a42; padding: 6px 10px; text-align: left; white-space: nowrap; }
.aps-md-table th { background: #2f2f35; color: #f3f3f5; font-weight: 700; }
.aps-md-table td { color: #d6d6dc; }
.aps-md-table tbody tr:nth-child(even) td { background: rgba(255, 255, 255, 0.03); }
.aps-chat-error { color: #fca5a5; font-size: 13px; margin-top: 8px; }
/* "Peter is thinking" indicator: three bouncing dots shown while waiting for the reply. */
.aps-thinking { display: inline-flex; align-items: center; gap: 10px; }
.aps-dots { display: inline-flex; align-items: center; gap: 4px; }
.aps-dots span { width: 6px; height: 6px; border-radius: 50%; background: #9aa0ae; display: inline-block;
  animation: aps-dot-bounce 1.2s infinite ease-in-out both; }
.aps-dots span:nth-child(1) { animation-delay: -0.24s; }
.aps-dots span:nth-child(2) { animation-delay: -0.12s; }
@keyframes aps-dot-bounce { 0%, 80%, 100% { transform: scale(0.5); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .aps-dots span { animation: none; opacity: 0.7; } }
.aps-input-wrap { display: flex; gap: 10px; margin-top: 14px; background: #262629; border: 1px solid #3a3a42;
  border-radius: 14px; padding: 8px 8px 8px 16px; align-items: flex-end; }
.aps-input { flex: 1; border: none; outline: none; resize: none; background: transparent; color: #e7e7ea;
  font-family: inherit; font-size: 14px; padding: 8px 0; max-height: 120px; }
.aps-input::placeholder { color: #7a7a82; }
/* Always-visible AI notice under the input: small and muted, but never hidden. */
.aps-disclaimer { margin: 8px 2px 0; font-size: 11px; line-height: 1.4; color: #8a8a92; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.aps-send { background: #3b6fe3; color: #fff; border: none; border-radius: 10px; width: 38px; height: 38px;
  font-size: 18px; cursor: pointer; flex-shrink: 0; }
.aps-send:disabled { background: #3a3a42; color: #6a6a72; cursor: not-allowed; }

/* Embedded mode: the chat fills the floating widget panel (the panel provides bg + size). */
.aps-embedded { padding: 0; margin: 0; max-width: none; min-height: 0; height: 100%; display: flex; }
.aps-embedded .aps-chat-step { max-width: 100%; margin: 0; border: none; border-radius: 0;
  flex: 1; min-height: 0; display: flex; flex-direction: column; padding: 12px 12px 10px; }
.aps-embedded .aps-chat-body { flex: 1; min-height: 0; display: flex; flex-direction: column; }
.aps-embedded .aps-chat { flex: 1; min-height: 0; max-height: none; padding: 14px; }
</style>
